'use client';

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { getChild, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import type { ChildDetails } from '@sola/schema/dotcom/children/child.ts';

type RandomChildContextValue = {
  data: Awaited<ChildDetails> | undefined;
  isLoading: boolean;
};

const RandomChildContext = createContext<RandomChildContextValue | null>(null);

export const useRandomChild = () => {
  const context = useContext(RandomChildContext);

  if (!context) {
    throw new Error('useRandomChild must be used within a RandomChildProvider');
  }

  return context;
};

export const RandomChildProvider = ({
  children,
  loadRandomChild = false,
}: {
  children: ReactNode;
  loadRandomChild?: boolean;
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['randomChild'],
    queryFn: async () => {
      const response = await getRandomChildren();
      const childId = response?.children?.[0]?.globalId || '';
      return await getChild(childId);
    },
    enabled: loadRandomChild,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const value = useMemo(() => ({ data, isLoading }), [data, isLoading]);

  return (
    <RandomChildContext.Provider value={value}>
      {children}
    </RandomChildContext.Provider>
  );
};
